// Generate a curated Alberta culinary itinerary from user preferences.
// Deterministic-ish: seeded by startingLocation + date. Used by both
// the wizard flow and the Spin-the-Wheel surprise mode.

import { ALBERTA_RESTAURANTS, ALBERTA_FOOD_TRUCKS } from '../data/restaurants';

const ALL_FOR_PICKS = [...ALBERTA_RESTAURANTS, ...ALBERTA_FOOD_TRUCKS];

const BUDGET_TO_PRICES = {
  Budget: ['$', '$$'],
  Moderate: ['$$', '$$$'],
  Premium: ['$$$', '$$$$'],
  Luxury: ['$$$$'],
};

// Edmonton/Calgary/Banff etc. all have neighbours — if starting in Calgary, we
// also allow Banff/Canmore/Priddis on longer trips.
const NEARBY = {
  Calgary: ['Calgary', 'Priddis', 'Canmore', 'Banff', 'Lake Louise', 'Okotoks', 'Cochrane', 'Airdrie', 'Turner Valley'],
  Edmonton: ['Edmonton', 'St. Albert', 'Sherwood Park', 'Leduc', 'Spruce Grove'],
  Banff: ['Banff', 'Canmore', 'Lake Louise'],
  Canmore: ['Canmore', 'Banff', 'Lake Louise'],
  Jasper: ['Jasper'],
  'Red Deer': ['Red Deer'],
  Lethbridge: ['Lethbridge', 'Waterton'],
  'Medicine Hat': ['Medicine Hat'],
};

function pickN(list, n, rng = Math.random) {
  const pool = [...list];
  const out = [];
  while (out.length < n && pool.length) {
    const i = Math.floor(rng() * pool.length);
    out.push(pool.splice(i, 1)[0]);
  }
  return out;
}

// Small seeded PRNG so identical wizard inputs produce the same itinerary
// (shareable/reproducible trips). xmur3 seed -> mulberry32 generator.
function makeRng(seedStr) {
  let h = 1779033703 ^ seedStr.length;
  for (let i = 0; i < seedStr.length; i++) {
    h = Math.imul(h ^ seedStr.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = (h ^= h >>> 16) >>> 0;
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function generateItinerary(formData) {
  const duration = parseInt(formData.tripDuration) || 3;
  const allowedCities = NEARBY[formData.startingLocation] || [formData.startingLocation];
  const allowedPrices = BUDGET_TO_PRICES[formData.budgetRange] || ['$$', '$$$'];
  const cuisines = new Set(formData.cuisineTypes || []);

  // Seed the RNG from the traveller's actual choices so the same inputs always
  // yield the same trip (reproducible + shareable). Header promise, now kept.
  const seedStr = [
    formData.startingLocation || '',
    formData.travelDates || '',
    formData.budgetRange || '',
    formData.tripDuration || '',
    [...cuisines].sort().join('|'),
    (formData.mustInclude || []).join('|'),
  ].join('::');
  const rng = makeRng(seedStr);

  // Score each restaurant on how well it matches preferences
  const scored = ALBERTA_RESTAURANTS
    .filter((r) => allowedCities.includes(r.city))
    .map((r) => {
      // Base score for every curated pick, then boost by how well it matches
      // the traveller's budget and cuisine choices. (Restaurants have no numeric
      // rating field, so a uniform base keeps the boosts meaningful.)
      let score = 10;
      if (allowedPrices.includes(r.price)) score += 40;
      if (cuisines.size === 0 || cuisines.has(r.cuisine)) score += 25;
      return { r, score };
    })
    .sort((a, b) => b.score - a.score);

  // Pick top 3×duration, then jitter a bit so trips differ run-to-run
  const scoredPicks = pickN(
    scored.slice(0, Math.min(scored.length, Math.max(12, duration * 4))),
    Math.min(duration * 3, 12),
    rng
  ).map((x) => x.r);

  // "Add to trip" must-include stops: the user explicitly chose these, so they
  // always lead the itinerary (city filter doesn't apply to them).
  const mustInclude = (formData.mustInclude || [])
    .map((id) => ALL_FOR_PICKS.find((r) => r.id === id))
    .filter(Boolean);
  const seen = new Set();
  const pickedRestaurants = [];
  for (const r of [...mustInclude, ...scoredPicks]) {
    if (!seen.has(r.id)) { seen.add(r.id); pickedRestaurants.push(r); }
  }

  // Food trucks bonus: 1 per trip if city matches
  const trucks = ALBERTA_FOOD_TRUCKS.filter((t) => allowedCities.includes(t.city));
  const pickedTrucks = trucks.length ? pickN(trucks, Math.min(2, trucks.length), rng) : [];

  // Build per-day plan (breakfast/lunch/dinner)
  const slots = ['Breakfast', 'Lunch', 'Dinner'];
  const days = [];
  let idx = 0;
  for (let d = 1; d <= duration; d++) {
    const day = { day: d, meals: [] };
    for (const slot of slots) {
      if (idx < pickedRestaurants.length) {
        day.meals.push({ slot, restaurant: pickedRestaurants[idx] });
        idx++;
      }
    }
    days.push(day);
  }

  return {
    title: `${formData.startingLocation} Culinary Journey · ${duration} day${duration > 1 ? 's' : ''}`,
    duration,
    restaurants: pickedRestaurants,
    foodTrucks: pickedTrucks,
    days,
    summary: {
      location: formData.startingLocation,
      duration: formData.tripDuration,
      groupSize: formData.groupSize,
      budget: formData.budgetRange,
      cuisines: [...cuisines],
    },
  };
}

// "Spin the Wheel" — pick a random Alberta city + random-but-sane defaults
export function surpriseMe() {
  const startingCities = ['Calgary', 'Edmonton', 'Banff', 'Canmore', 'Jasper', 'Red Deer', 'Lethbridge'];
  const budgets = ['Moderate', 'Moderate', 'Premium']; // bias toward moderate
  const durations = ['3 Days', '4 Days', '3 Days', '5 Days'];
  const cuisines = ['Fine Dining', 'Farm-to-Table', 'Food Trucks', 'Indigenous Cuisine', 'International', 'Casual Dining'];
  const picked = pickN(cuisines, 2 + Math.floor(Math.random() * 2));
  return {
    startingLocation: startingCities[Math.floor(Math.random() * startingCities.length)],
    tripDuration: durations[Math.floor(Math.random() * durations.length)],
    travelDates: '',
    groupSize: '2 People',
    budgetRange: budgets[Math.floor(Math.random() * budgets.length)],
    cuisineTypes: picked,
    dietaryRestrictions: '',
    experienceLevel: 'Adventurous',
    accommodationType: '',
    transportationMode: '',
    specialRequests: 'Surprise me!',
    contactEmail: '',
    contactPhone: '',
  };
}
